import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

// Import configs and util modules
import { FormattedMessage } from '../../../../util/reactIntl';
import { LISTING_STATE_DRAFT } from '../../../../util/types';

// Import shared components
import { ExternalLink, H3, IconSpinner, ListingLink, NamedLink } from '../../../../components';

// Import modules from this directory
import EditListingLocationForm from './EditListingLocationForm';
import css from './EditListingLocationPanel.module.css';
import useInterfaceLang from '../../../../hooks/useInterfaceLang';

const getInitialValues = (props, interfaceLang) => {
  const locale = interfaceLang.split('-')[0];

  const { listing } = props;
  const { geolocation, publicData } = listing?.attributes || {};

  // Only render current search if full place object is available in the URL params
  // TODO bounds are missing - those need to be queried directly from Google Places
  const locationFieldsPresent = publicData?.location?.address && geolocation;
  const { location } = publicData || {};
  const { address } = location || {};

  const addressName =
    typeof address === 'object'
      ? address?.placeNames?.[locale] ?? address?.placeNames?.default
      : address;

  return {
    location: locationFieldsPresent
      ? {
          search: addressName,
          selectedPlace: { address, origin: geolocation },
        }
      : null,
  };
};

/**
 * The EditListingLocationPanel component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {propTypes.ownListing} props.listing - The listing object
 * @param {boolean} props.disabled - Whether the form is disabled
 * @param {boolean} props.ready - Whether the form is ready
 * @param {Function} props.onSubmit - The submit function
 * @param {string} props.submitButtonText - The submit button text
 * @param {boolean} props.panelUpdated - Whether the panel is updated
 * @param {boolean} props.updateInProgress - Whether the update is in progress
 * @param {Object} props.errors - The errors object
 * @returns {JSX.Element}
 */
const EditListingLocationPanel = props => {
  const { interfaceLang } = useInterfaceLang();
  // State is needed since LocationAutocompleteInput doesn't have internal state
  // and therefore re-rendering would overwrite the values during XHR call.
  const [state, setState] = useState({ initialValues: getInitialValues(props, interfaceLang) });
  const {
    className,
    rootClassName,
    listing,
    disabled,
    ready,
    onSubmit,
    submitButtonText,
    panelUpdated,
    updateInProgress,
    errors,
    onGoBackATab,
    marketplaceCurrency,
    listingMinimumPriceSubUnits,
    currentUser,
    changeSubscriptionURL,
    changeSubscriptionInProgress,
    changeSubscriptionError,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const isPublished = listing?.id && listing?.attributes.state !== LISTING_STATE_DRAFT;
  const unitType = listing?.attributes?.publicData?.unitType;

  const { subscription } = currentUser?.attributes?.profile?.metadata || {};
  const { numberOfListingsAllowed, numberOfListingsPublished } = subscription || {};
  const isExceedingLimit =
    numberOfListingsAllowed !== null &&
    (!subscription || numberOfListingsAllowed <= numberOfListingsPublished);

  useEffect(() => {
    setState({ initialValues: getInitialValues(props, interfaceLang) });
  }, [interfaceLang]);

  return (
    <div className={classes}>
      <H3 as="h1">
        {isPublished ? (
          <FormattedMessage
            id="EditListingLocationPanel.title"
            values={{ listingTitle: <ListingLink listing={listing} />, lineBreak: <br /> }}
          />
        ) : (
          <FormattedMessage
            id="EditListingLocationPanel.locationAndPrice"
            values={{ lineBreak: <br /> }}
          />
        )}
      </H3>
      {changeSubscriptionInProgress ? (
        <IconSpinner />
      ) : (
        <EditListingLocationForm
          className={css.form}
          initialValues={state.initialValues}
          onSubmit={values => {
            const {
              location,
              submitAction,
              navigateTo,
            } = values;
            const {
              selectedPlace: { address, origin },
            } = location;

            const locale = interfaceLang.split('-')[0];
            const addressName =
              typeof address === 'object'
                ? address?.placeNames?.[locale] ?? address?.placeNames?.default
                : address;

            // New values for listing attributes
            const updateValues = {
              geolocation: origin,
              publicData: {
                id: listing.id.uuid,
                location: { address },
              },
            };
            // Save the initialValues to state
            // LocationAutocompleteInput doesn't have internal state
            // and therefore re-rendering would overwrite the values during XHR call.
            setState({
              initialValues: {
                location: { search: addressName, selectedPlace: { address, origin } },
              },
            });
            onSubmit(updateValues, !!(submitAction === 'saveAndPublish'), navigateTo);
          }}
          saveActionMsg={submitButtonText}
          disabled={disabled}
          ready={ready}
          updated={panelUpdated}
          updateInProgress={updateInProgress}
          fetchErrors={errors}
          onGoBackATab={onGoBackATab}
          disableSubmitOnly={isExceedingLimit && !isPublished}
          listing={listing}
          autoFocus
        />
      )}
      {isExceedingLimit && !isPublished && !changeSubscriptionInProgress && (
        <FormattedMessage
          id="EditListingLocationPanel.limitExceeded"
          values={{
            link: changeSubscriptionURL ? (
              <ExternalLink href={changeSubscriptionURL}>
                <FormattedMessage id="Global.stripeCustomerPortal" />
              </ExternalLink>
            ) : (
              <NamedLink name="ManageListingsPage">
                <FormattedMessage id="Global.stripeCustomerPortal" />
              </NamedLink>
            ),
          }}
        />
      )}
    </div>
  );
};

export default EditListingLocationPanel;
